// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Visit prep note review. Renders the CANONICAL, backend-generated note
// (GET /v1/intake/{id}/prep-note) — never invented content. Generation is async, so
// a 404 means "not ready yet": we poll briefly. The patient confirms the note is
// accurate and sends it to their provider. No PHI is persisted client-side.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, Sparkles, AlertTriangle } from 'lucide-react'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { Logo } from '@/components/Logo'
import { Markdown } from '@/components/Markdown'
import { cn } from '@/lib/utils'
import { isKioskSession } from '@/lib/kiosk-mode'
import { ApiError, isAuthError } from '@/lib/api'
import { getConversationId, getPatientFirstName } from '@/lib/session'
import { formatDisplayDateTime } from '@/lib/date-format'
import { getPrepNote, submitPrepNote } from '@/intake/intake-service'
import type { PrepNote, PrepNoteSection } from '@/intake/types'

// ── State machine ─────────────────────────────────────────────────────────────

type ScreenState = 'loading' | 'not_ready' | 'ready' | 'error' | 'sending' | 'done'

// The note is generated a couple seconds after the conversation settles. Poll a
// bounded number of times on 404 before offering a manual retry.
const NOT_READY_POLL_MS = 2500
const NOT_READY_MAX_TRIES = 12 // ~30s

function noteHasContent(note: PrepNote): boolean {
  return Boolean(note.chiefComplaint || note.summary || note.sections?.length)
}

// ── Section card ──────────────────────────────────────────────────────────────

function SectionCard({ section, dimmed }: { section: PrepNoteSection; dimmed: boolean }) {
  const hasContent = Boolean(section.content?.trim())
  return (
    <div
      className={cn(
        'bg-card border border-border rounded-2xl overflow-hidden transition-opacity duration-300',
        dimmed && 'opacity-50',
      )}
    >
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-secondary/30">
        <p className="text-[13px] font-semibold text-foreground">{section.title || section.sectionKey}</p>
      </div>
      <div className="px-5 py-4">
        {hasContent ? (
          <Markdown content={section.content} />
        ) : (
          <p className="text-[14px] text-muted-foreground">—</p>
        )}
      </div>
    </div>
  )
}

// ── Centered status panel (loading / not-ready / error / empty) ─────────────────

function StatusPanel({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode
  title: string
  body: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-16 max-w-sm mx-auto">
      <div className="mb-5">{icon}</div>
      <h2 className="text-xl font-bold text-foreground mb-2">{title}</h2>
      <p className="text-muted-foreground text-[15px] leading-relaxed mb-6">{body}</p>
      {action}
    </div>
  )
}

// ── Done screen ─────────────────────────────────────────────────────────────

function formatSubmittedAt(iso: string | undefined): string | null {
  const formatted = formatDisplayDateTime(iso, { fallback: '' })
  return formatted || null
}

function DoneScreen({ firstName, submittedAt }: { firstName: string; submittedAt?: string }) {
  const { t } = useTranslation()
  const submitted = formatSubmittedAt(submittedAt)
  return (
    <div className="flex flex-col items-center text-center px-6 py-12 max-w-sm mx-auto">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center shadow-2xl shadow-primary/30">
          <Check className="w-10 h-10 text-white" strokeWidth={2.5} />
        </div>
        <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-card border border-border flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-[#02C7C3]" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-3">
        {firstName ? t('prepNote.doneGreetingName', { name: firstName }) : t('prepNote.doneGreeting')}
      </h2>
      <p className="text-muted-foreground text-[15px] leading-relaxed mb-6">{t('prepNote.doneBodyGeneric')}</p>
      {submitted && (
        <p className="text-[13px] text-muted-foreground mb-8">{t('prepNote.submittedAtLine', { time: submitted })}</p>
      )}
      <p className="text-[12px] text-muted-foreground">{t('prepNote.closeNote')}</p>
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────

export function PrepNoteReviewScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [screenState, setScreenState] = useState<ScreenState>('loading')
  const [note, setNote] = useState<PrepNote | null>(null)
  const [sendFailed, setSendFailed] = useState(false)

  const triesRef = useRef(0)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firstName = getPatientFirstName() ?? ''

  const load = useCallback(async () => {
    const id = getConversationId()
    if (!id) {
      navigate('/chat', { replace: true })
      return
    }
    try {
      const fetched = await getPrepNote(id)
      setNote(fetched)
      setScreenState('ready')
    } catch (err) {
      if (isAuthError(err)) {
        navigate('/verify', { replace: true })
        return
      }
      // 404 → the note is still being generated. Poll until it appears or we
      // hit the cap, then offer a manual retry.
      if (err instanceof ApiError && err.status === 404) {
        if (triesRef.current < NOT_READY_MAX_TRIES) {
          triesRef.current += 1
          setScreenState('not_ready')
          pollRef.current = setTimeout(() => void load(), NOT_READY_POLL_MS)
        } else {
          setScreenState('not_ready')
        }
        return
      }
      setScreenState('error')
    }
  }, [navigate])

  useEffect(() => {
    void load()
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [load])

  function retry() {
    triesRef.current = 0
    setScreenState('loading')
    void load()
  }

  async function handleSend() {
    const id = getConversationId()
    if (!id) return
    setSendFailed(false)
    setScreenState('sending')
    try {
      // "Send to my provider" is the step that actually delivers the note: it
      // transitions draft → submitted (the provider only sees submitted notes) and
      // stamps submittedAt. Idempotent server-side. A 404 here means the note is
      // gone/never existed — surface it rather than pretend success.
      const submitted = await submitPrepNote(id)
      setNote(submitted)
      if (isKioskSession()) {
        navigate('/kiosk/done')
      } else {
        setScreenState('done')
      }
    } catch {
      setSendFailed(true)
      setScreenState('ready')
    }
  }

  const isSending = screenState === 'sending'
  const showReview = screenState === 'ready' || screenState === 'sending'
  const hasContent = note ? noteHasContent(note) : false

  const header = (
    <div className="sticky top-0 z-30 bg-gradient-to-r from-[#012031] via-[#033C85] to-[#006EF9] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#02C7C3]/20 blur-2xl" />
      </div>
      <div className="relative z-10 px-5 py-4 md:px-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showReview && (
            <button
              onClick={() => navigate('/chat')}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label={t('prepNote.backToConversation')}
            >
              ‹
            </button>
          )}
          <div style={{ filter: 'brightness(0) invert(1)' }}>
            <Logo variant="mark" className="h-7 w-auto" aria-hidden />
          </div>
          <p className="text-white font-semibold text-sm leading-tight">
            {screenState === 'done' ? t('prepNote.submittedLabel') : t('prepNote.title')}
          </p>
        </div>
      </div>
    </div>
  )

  return (
    <OnboardingLayout>
      {/* Sticky header + CTA footer stay pinned to the viewport; the note body
          scrolls between them. */}
      <div className="flex flex-col min-h-svh">
        {header}

        <div className="flex-1">
          {screenState === 'loading' && (
            <StatusPanel
              icon={<Loader2 className="w-10 h-10 text-primary animate-spin" />}
              title={t('prepNote.loadingTitle')}
              body={t('prepNote.loadingBody')}
            />
          )}

          {screenState === 'not_ready' && (
            <StatusPanel
              icon={<Loader2 className="w-10 h-10 text-primary animate-spin" />}
              title={t('prepNote.notReadyTitle')}
              body={t('prepNote.notReadyBody')}
              action={
                triesRef.current >= NOT_READY_MAX_TRIES ? (
                  <button
                    onClick={retry}
                    className="h-11 px-6 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 transition-opacity"
                  >
                    {t('prepNote.retry')}
                  </button>
                ) : undefined
              }
            />
          )}

          {screenState === 'error' && (
            <StatusPanel
              icon={<AlertTriangle className="w-10 h-10 text-red-500" />}
              title={t('prepNote.errorTitle')}
              body={t('prepNote.errorBody')}
              action={
                <button
                  onClick={retry}
                  className="h-11 px-6 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 transition-opacity"
                >
                  {t('prepNote.retry')}
                </button>
              }
            />
          )}

          {screenState === 'done' && <DoneScreen firstName={firstName} submittedAt={note?.submittedAt} />}

          {showReview && note && !hasContent && (
            <StatusPanel
              icon={<AlertTriangle className="w-10 h-10 text-amber-500" />}
              title={t('prepNote.emptyTitle')}
              body={t('prepNote.emptyBody')}
              action={
                <button
                  onClick={() => navigate('/chat')}
                  className="h-11 px-6 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 transition-opacity"
                >
                  {t('prepNote.backToChat')}
                </button>
              }
            />
          )}

          {showReview && note && hasContent && (
            <div className="max-w-xl mx-auto px-5 py-6 md:px-8 md:py-8 flex flex-col gap-4 pb-8">
              {!isSending && (
                <div className="text-center mb-2">
                  <p className="text-[14px] text-muted-foreground leading-relaxed">{t('prepNote.subtitle')}</p>
                </div>
              )}

              {isSending && (
                <div className="text-center py-2">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <p className="text-sm font-semibold text-foreground">{t('prepNote.sending')}</p>
                  </div>
                  <p className="text-[12px] text-muted-foreground">{t('prepNote.sendingNote')}</p>
                </div>
              )}

              {note.chiefComplaint && (
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border bg-secondary/30">
                    <p className="text-[13px] font-semibold text-foreground">{t('prepNote.chiefComplaint')}</p>
                  </div>
                  <div className="px-5 py-4">
                    <p className="text-[14px] text-foreground leading-relaxed">{note.chiefComplaint}</p>
                  </div>
                </div>
              )}

              {note.summary && (
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border bg-secondary/30">
                    <p className="text-[13px] font-semibold text-foreground">{t('prepNote.summaryLabel')}</p>
                  </div>
                  <div className="px-5 py-4">
                    <Markdown content={note.summary} />
                  </div>
                </div>
              )}

              {[...(note.sections ?? [])]
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((section) => (
                  <SectionCard key={section.sectionKey} section={section} dimmed={isSending} />
                ))}
            </div>
          )}
        </div>

        {/* Pinned CTA — only in the review flow with content to send. */}
        {showReview && note && hasContent && (
          <div className="sticky bottom-0 z-30 border-t border-border bg-card px-5 py-4 md:px-8">
            <div className="max-w-xl mx-auto">
              {sendFailed && (
                <p className="text-[12px] text-red-500 text-center mb-2">{t('prepNote.sendFailedBody')}</p>
              )}
              <button
                onClick={handleSend}
                disabled={isSending}
                className={cn(
                  'w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-lg shadow-primary/20 transition-opacity',
                  isSending ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 active:opacity-80',
                )}
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> {t('prepNote.submittingButton')}
                  </>
                ) : (
                  t('prepNote.sendButton')
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </OnboardingLayout>
  )
}
